// Game Service - Manages game state and business logic

class GameService {
  constructor() {
    this.gameState = null;
    this.playerID = null;
    this.sessionID = null;
    this.roomCode = null;
    this.playerName = null;
    this.listeners = [];

    // Load session from localStorage on init
    this.loadSession();
  }

  // Load session from localStorage
  loadSession() {
    try {
      const sessionID = localStorage.getItem('harbor_session_id');
      const playerID = localStorage.getItem('harbor_player_id');
      const roomCode = localStorage.getItem('harbor_room_code');
      const playerName = localStorage.getItem('harbor_player_name');

      if (sessionID) {
        this.sessionID = sessionID;
      }
      if (playerID) {
        this.playerID = playerID;
      }
      if (roomCode) {
        this.roomCode = roomCode;
      }
      if (playerName) {
        this.playerName = playerName;
      }
    } catch (err) {
      console.error('Error loading session from localStorage:', err);
    }
  }

  // Save session to localStorage
  saveSession() {
    try {
      if (this.sessionID) {
        localStorage.setItem('harbor_session_id', this.sessionID);
      }
      if (this.playerID) {
        localStorage.setItem('harbor_player_id', this.playerID);
      }
      if (this.roomCode) {
        localStorage.setItem('harbor_room_code', this.roomCode);
      }
      if (this.playerName) {
        localStorage.setItem('harbor_player_name', this.playerName);
      }
    } catch (err) {
      console.error('Error saving session to localStorage:', err);
    }
  }

  // Subscribe to state changes
  subscribe(callback) {
    this.listeners.push(callback);
    return () => {
      this.listeners = this.listeners.filter(l => l !== callback);
    };
  }

  // Notify all listeners of state change
  notifyListeners() {
    this.listeners.forEach(callback => callback(this.gameState));
  }

  // Set player info
  setPlayerInfo(playerID, playerName, roomCode) {
    this.playerID = playerID;
    this.playerName = playerName;
    this.roomCode = roomCode;
    this.saveSession();
  }

  // Set session ID
  setSessionID(sessionID) {
    this.sessionID = sessionID;
    this.saveSession();
  }

  // Update game state
  updateGameState(newState) {
    this.gameState = {
      ...this.gameState,
      ...newState
    };
    this.notifyListeners();
  }

  // Get current state
  getState() {
    return this.gameState;
  }

  // Get numeric value for a card
  getCardValue(card) {
    if (!card) return 0;

    // Special rule: Red King has value 0
    if (card.value === 'King' && (card.suit === 'Hearts' || card.suit === 'Diamonds')) {
      return 0;
    }

    const values = {
      'Ace': 1,
      'King': 13,
      'Queen': 12,
      'Jack': 11,
      '10': 10,
      '9': 9,
      '8': 8,
      '7': 7,
      '6': 6,
      '5': 5,
      '4': 4,
      '3': 3,
      '2': 2
    };

    return values[card.value] || 0;
  }

  // Get card display string
  getCardDisplay(card) {
    if (!card) return '';

    const symbols = {
      'Hearts': '♥',
      'Diamonds': '♦',
      'Clubs': '♣',
      'Spades': '♠'
    };

    return `${card.value}${symbols[card.suit] || ''}`;
  }

  // Get card color
  getCardColor(card) {
    if (!card) return 'black';
    return (card.suit === 'Hearts' || card.suit === 'Diamonds') ? 'red' : 'black';
  }

  // Check if it's current player's turn
  isMyTurn() {
    if (!this.gameState || !this.playerID) return false;
    return this.gameState.currentPlayer === this.playerID;
  }

  // Get current player info
  getMyPlayerState() {
    if (!this.gameState || !this.playerID) return null;
    return this.gameState.players?.find(p => p.id === this.playerID);
  }

  // Get other players (alive only)
  getOtherPlayers() {
    if (!this.gameState || !this.playerID) return [];
    return this.gameState.players?.filter(p => p.id !== this.playerID && p.isAlive) || [];
  }

  // Check if player is alive
  isAlive() {
    const myState = this.getMyPlayerState();
    return myState?.isAlive || false;
  }

  // Reset service
  reset() {
    this.gameState = null;
    this.playerID = null;
    this.sessionID = null;
    this.roomCode = null;
    this.playerName = null;

    // Clear localStorage
    try {
      localStorage.removeItem('harbor_session_id');
      localStorage.removeItem('harbor_player_id');
      localStorage.removeItem('harbor_room_code');
      localStorage.removeItem('harbor_player_name');
    } catch (err) {
      console.error('Error clearing session from localStorage:', err);
    }

    this.notifyListeners();
  }

  // Alias for reset() - clears session data
  clearSession() {
    this.reset();
  }
}

// Singleton instance
const gameService = new GameService();

export default gameService;
